'use client';

/* eslint-disable @typescript-eslint/no-unused-vars */

import type {
  AttributesEntity,
  Package,
  ProductDetailData,
  ProductDisplayData,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import type {
  Product,
  RelatedProductsEntity,
} from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { SelectedValue } from '@eliadmin/core/src/stores/category-store';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import {
  getProductMinQuantity,
  getProductQuantityRules,
  normalizeProductQuantity,
} from '@eliadmin/core/src/utils/product-utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

export type SelectedServices = {
  id: number;
  price_with_vat: number;
  price: number;
};

type VariantStoreState = {
  product: ProductDetailsWithSlug;

  selectedVariant?: VariantsEntity;
  selectedValues: SelectedValue[];
  selectedQuantity: number;
  selectedPackages: Package[];
  selectedServices: SelectedServices[];
  selectedAccessories?: RelatedProductsEntity[];
  accessoriesQuantities?: Record<number, number>;
  customValues: any[];
  customValuesError: boolean;
};

export type CustomAttributeValue = {
  id: number;
  name: string;
  value: string | number;
  unit: string;
};
export type ProductDetailsWithSlug = ProductDisplayData;

type VariantStoreActions = {
  reset: () => void;
  updateSelectedValue: (values: SelectedValue) => void;
  setSelectedQuantity: (quantity: number) => void;
  setSelectedValues: (values: SelectedValue[]) => void;
  setSelectedPackages: (packages: Package[]) => void;
  updateSelectedPackages: (packages: Package) => void;
  setSelectedVariant: (variant: VariantsEntity | null) => void;
  setSelectedServices: (services: SelectedServices[]) => void;
  updateSelectedServices: (services: SelectedServices) => void;
  updateSelectedAccessories: (accessories: RelatedProductsEntity) => void;
  updateAccessoriesQuantities: (quantities: Record<number, number>) => void;
  updateCustomValue: (values: any) => void;
  setCustomValues: (values: any[]) => void;
  setCustomValuesError: (error: boolean) => void;
};

const [VariantsProvider, useVariantsStore] = createContextStore<
  VariantStoreState,
  VariantStoreActions
>(
  {
    product: {} as ProductDetailsWithSlug,
    selectedValues: [],
    selectedQuantity: 1,
    selectedVariant: undefined,
    selectedPackages: [],
    selectedServices: [],
    selectedAccessories: [],
    accessoriesQuantities: {},
    customValues: [],
    customValuesError: false,
  },
  (set, get) => ({
    reset: () => {
      set(state => {
        state.selectedVariant = undefined;
        state.selectedValues = [];
        state.selectedQuantity = 1;
        state.selectedPackages = [];
        state.selectedServices = [];
        state.selectedAccessories = [];
        state.accessoriesQuantities = {};
        state.customValues = [];
        state.customValuesError = false;
      });
    },
    updateSelectedValue: value => {
      set(state => {
        const selectedValue = state.selectedValues.find(
          (selectedValue: SelectedValue) => selectedValue.attribute.id === value.attribute.id,
        );

        if (selectedValue && selectedValue.value.id === value.value.id) {
          state.selectedValues = state.selectedValues.filter(
            (selectedValue: SelectedValue) => selectedValue.value.id !== value.value.id,
          );
        } else if (selectedValue) {
          selectedValue.value = value.value;
        } else {
          state.selectedValues.push(value);
        }

        const newSelectedVariant = findSelectedVariant(
          get().product.variants!,
          state.selectedValues,
        );

        if (!newSelectedVariant) {
          const newSelectedVariant2 = getVariantWithSpecificAttributes(
            get().product.variants!,
            state.selectedValues,
          );

          if (newSelectedVariant2) {
            state.selectedVariant = newSelectedVariant2;
            state.selectedQuantity =
              newSelectedVariant2.quantity > 0
                ? getProductMinQuantity(getProductQuantityRules(state.product, newSelectedVariant2))
                : 0;
            return;
          }

          if (!newSelectedVariant2) {
            const closestVariant = getClosestVariant(get().product.variants!, state.selectedValues);
            if (!closestVariant) return;
            state.selectedVariant = closestVariant;
            state.selectedValues = getSelectedValuesForVariant(
              closestVariant,
              get().product.attributes!,
              state.selectedValues,
            );
            state.selectedQuantity =
              closestVariant.quantity > 0
                ? getProductMinQuantity(getProductQuantityRules(state.product, closestVariant))
                : 0;
            return;
          }

          state.selectedQuantity = 0;
          state.selectedVariant = undefined;
          return;
        }
        state.selectedVariant = newSelectedVariant;
        state.selectedQuantity =
          newSelectedVariant.quantity > 0 ? 1 : newSelectedVariant.availability.sellable ? 1 : 0;
      });
    },
    setSelectedQuantity: quantity => {
      set(state => {
        if (!state.selectedVariant && !!state.product?.variants?.length) {
          state.selectedQuantity = 0;
          return;
        }
        state.selectedQuantity =
          quantity > 0
            ? normalizeProductQuantity(
                quantity,
                getProductQuantityRules(state.product, state.selectedVariant),
              )
            : quantity;
      });
    },
    setSelectedValues: values => {
      set(state => {
        state.selectedValues = values;
        const newSelectedVariant = findSelectedVariant(get().product.variants!, values);

        if (!newSelectedVariant) {
          const closestVariant = getClosestVariant(get().product.variants!, values);
          if (!closestVariant) return;
          state.selectedVariant = closestVariant;
          state.selectedValues = getSelectedValuesForVariant(
            closestVariant,
            get().product.attributes!,
            values,
          );
          state.selectedQuantity =
            closestVariant.quantity > 0
              ? getProductMinQuantity(getProductQuantityRules(state.product, closestVariant))
              : 0;
          return;
        }

        state.selectedVariant = newSelectedVariant;
        state.selectedQuantity =
          newSelectedVariant?.quantity > 0
            ? getProductMinQuantity(getProductQuantityRules(state.product, newSelectedVariant))
            : 0;
      });
    },
    setSelectedVariant: variant => {
      set(state => {
        if (!variant) {
          state.selectedVariant = undefined;
          state.selectedValues = [];
          state.selectedQuantity = 0;
          return;
        }

        state.selectedVariant = variant;
        state.selectedValues = getSelectedValuesForVariant(
          variant,
          get().product.attributes!,
          state.selectedValues,
        );
        state.selectedQuantity =
          variant.quantity > 0 || variant.availability?.sellable
            ? getProductMinQuantity(getProductQuantityRules(state.product, variant))
            : 0;
      });
    },
    setSelectedPackages: values => {
      set(state => {
        if (process.env.NODE_ENV === 'development') console.log(values);
      });
    },
    updateSelectedPackages: values => {
      set(state => {
        if (state.selectedPackages.find(value => value.id === values.id)) {
          state.selectedPackages = state.selectedPackages.filter(value => value.id !== values.id);
        } else {
          state.selectedPackages.push(values);
        }
      });
    },
    setSelectedServices: values => {
      set(state => {
        if (process.env.NODE_ENV === 'development') console.log(values);
      });
    },
    updateSelectedServices: values => {
      set(state => {
        if (state.selectedServices.find(value => value.id === values.id)) {
          state.selectedServices = state.selectedServices.filter(value => value.id !== values.id);
        } else {
          state.selectedServices.push(values);
        }
      });
    },
    updateSelectedAccessories: value => {
      set(state => {
        if (!state.selectedAccessories) {
          state.selectedAccessories = [];
        }
        const index = state.selectedAccessories.findIndex(accessory => accessory.id === value.id);
        if (index !== -1) {
          state.selectedAccessories.splice(index, 1);
        } else {
          state.selectedAccessories.push(value);
        }
      });
    },
    updateAccessoriesQuantities: quantities => {
      set(state => {
        state.accessoriesQuantities = {
          ...state.accessoriesQuantities,
          ...quantities,
        };
      });
    },
    updateCustomValue: value => {
      set(state => {
        const index = state.customValues.findIndex(cv => cv.id === value.id);
        if (index !== -1) {
          state.customValues[index] = value;
        } else {
          state.customValues.push(value);
        }
      });
    },
    setCustomValues: values => {
      set(state => {
        state.customValues = values;
      });
    },
    setCustomValuesError: error => {
      set(state => {
        state.customValuesError = error;
      });
    },
  }),
);

export { VariantsProvider, useVariantsStore };
export type { VariantStoreState };

// helpers for synchronization with query
function syncVariantIdToParams(
  selectedVariant: VariantsEntity | undefined,
  searchParams: URLSearchParams,
) {
  searchParams.delete('vid');

  if (selectedVariant?.id) {
    searchParams.set('vid', String(selectedVariant.id));
  }
}

function parseAttributesQuery(attributes: AttributesEntity[], query: string): SelectedValue[] {
  const searchParams = new URLSearchParams(query);
  const selectedValues: SelectedValue[] = [];
  attributes.forEach(attribute => {
    const valueIds = searchParams.getAll(`attrs[${attribute.id}]`);
    if (!valueIds?.length) return;
    const values = attribute.values?.filter(value => valueIds.includes(String(value.id)));
    if (!values?.length) return;
    for (const value of values) {
      selectedValues.push({ attribute, value });
    }
  });

  return selectedValues;
}

/**
 * Variant is selected only if all selected values are in variant
 */
function findSelectedVariant(variants: VariantsEntity[], selectedValues: SelectedValue[]) {
  for (const variant of variants) {
    if (!variant.values) continue;
    const variantAttributes = variant.values.reduce((acc, value) => {
      if (!acc.has(value.attribute_id)) {
        acc.set(value.attribute_id, []);
      }
      acc.get(value.attribute_id)?.push(value);
      return acc;
    }, new Map<number, VariantsEntity['values']>());

    const matchedAttributes = new Set<number>();

    for (const selectedValue of selectedValues) {
      const variantValues = variantAttributes.get(selectedValue.attribute.id);
      if (!variantValues) continue;
      if (!variantValues.some(variantValue => variantValue.id === selectedValue.value.id)) {
        continue;
      }
      matchedAttributes.add(selectedValue.attribute.id);
    }

    if (matchedAttributes.size === variantAttributes.size) {
      return variant;
    }
  }

  return null;
}
// if needed move utils like this to separate files, so we can use it in server components
export function sortVariantsByAvailability(variants: VariantsEntity[]) {
  return [...variants].sort((a, b) => {
    if (a.quantity > 0 && b.quantity <= 0) {
      return -1;
    }

    if (b.quantity > 0 && a.quantity <= 0) {
      return 1;
    }

    if (a.availability.sellable && !b.availability.sellable) {
      return -1;
    }

    if (b.availability.sellable && !a.availability.sellable) {
      return 1;
    }

    return 0;
  });
}

function getClosestVariant(
  variants: VariantsEntity[],
  selectedValues: SelectedValue[],
): VariantsEntity | undefined {
  let closestVariant: VariantsEntity | null = null;

  let mostMatchedAttributes = 0;
  for (const variant of variants) {
    if (!variant.values) continue;
    const variantAttributes = variant.values.reduce((acc, value) => {
      if (!acc.has(value.attribute_id)) {
        acc.set(value.attribute_id, []);
      }
      acc.get(value.attribute_id)?.push(value);
      return acc;
    }, new Map<number, VariantsEntity['values']>());

    const matchedAttributes = new Set<number>();

    for (const selectedValue of selectedValues) {
      const variantValues = variantAttributes.get(selectedValue.attribute.id);
      if (!variantValues) continue;
      if (!variantValues.some(variantValue => variantValue.id === selectedValue.value.id)) {
        continue;
      }
      matchedAttributes.add(selectedValue.attribute.id);
    }

    if (matchedAttributes.size > mostMatchedAttributes) {
      mostMatchedAttributes = matchedAttributes.size;
      closestVariant = variant;
    }
  }

  return closestVariant ?? variants[0];
}

function getSelectedValuesForVariant(
  variant: VariantsEntity,
  attributes: AttributesEntity[],
  alreadySelectedValues: SelectedValue[],
) {
  const variantAttributes =
    variant.values?.reduce((acc, value) => {
      if (!acc.has(value.attribute_id)) {
        acc.set(value.attribute_id, []);
      }
      acc.get(value.attribute_id)?.push(value);
      return acc;
    }, new Map<number, VariantsEntity['values']>()) ?? new Map<number, VariantsEntity['values']>();

  const selectedValues: SelectedValue[] = [];

  for (const attribute of attributes) {
    const variantValues = variantAttributes.get(attribute.id!);
    if (!variantValues) continue;
    const selectedValue = alreadySelectedValues.find(
      selectedValue => selectedValue.attribute.id === attribute.id,
    );
    if (selectedValue) {
      selectedValues.push(selectedValue);
    } else {
      const value = attribute.values!.find(value => value.id === variantValues[0].id)!;
      if (!value) continue;
      selectedValues.push({
        attribute,
        value,
      });
    }
  }

  return selectedValues;
}

function getVariantWithSpecificAttributes(
  variants: VariantsEntity[],
  selectedValues: SelectedValue[],
): VariantsEntity | undefined {
  const selectedValues2 = JSON.parse(JSON.stringify(selectedValues));
  for (const variant of variants) {
    if (!variant.values) continue;

    const variantAttributes = variant.values.reduce((acc, value) => {
      if (!acc.has(value.attribute_id)) {
        acc.set(value.attribute_id, []);
      }
      acc.get(value.attribute_id)?.push(value);
      return acc;
    }, new Map<number, VariantsEntity['values']>());

    // Check if attributes 29 and 30 match
    const requiredAttributes = [29, 30];
    let isMatch = true;

    for (const attributeId of requiredAttributes) {
      const selectedValue = selectedValues2.find(
        (value: any) => value.attribute.id === attributeId,
      );
      const variantValues = variantAttributes.get(attributeId);

      // If no selected value or no match in the variant, it's not a match
      if (!selectedValue || !variantValues?.some(v => v.id === selectedValue.value.id)) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      return variant; // Found a matching variant
    }
  }

  for (const variant of variants) {
    if (!variant.values) continue;

    const variantAttributes = variant.values.reduce((acc, value) => {
      if (!acc.has(value.attribute_id)) {
        acc.set(value.attribute_id, []);
      }
      acc.get(value.attribute_id)?.push(value);
      return acc;
    }, new Map<number, VariantsEntity['values']>());

    // Check if attributes 29 and 30 match
    const requiredAttributes = [30];
    let isMatch = true;

    for (const attributeId of requiredAttributes) {
      const selectedValue = selectedValues2.find(
        (value: any) => value.attribute.id === attributeId,
      );
      const variantValues = variantAttributes.get(attributeId);

      // If no selected value or no match in the variant, it's not a match
      if (!selectedValue || !variantValues?.some(v => v.id === selectedValue.value.id)) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      return variant; // Found a matching variant
    }
  }

  return undefined; // No matching variant found
}

export function VariantsParamsSync() {
  const {
    selectedVariant,
    selectedValues,
    selectedQuantity,
    setSelectedValues,
    setSelectedVariant,
    setSelectedQuantity,
    updateAccessoriesQuantities,
    product,
    accessoriesQuantities,
  } = useVariantsStore();

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isMounted, setMounted] = useState(false);

  const queryString = searchParams.toString();

  // Parse query and update store. This must also run after client navigation, where
  // the product page can mount before the new search params have fully synchronized.
  useEffect(() => {
    const queryQuantity = Number(searchParams.get('quantity'));
    const selectedQuantity =
      Number.isFinite(queryQuantity) && queryQuantity > 0
        ? queryQuantity
        : getProductMinQuantity(product);
    const variantId = searchParams.get('vid');

    const querySelectedVariant = variantId
      ? product.variants!.find(variant => variant.id === Number(variantId))
      : null;

    if (querySelectedVariant) {
      setSelectedVariant(querySelectedVariant);
    } else {
      const selectedValues = parseAttributesQuery(product.attributes!, queryString);
      setSelectedValues(selectedValues);
    }

    // Variant selection resets quantity to its minimum. Restore the URL quantity
    // afterwards, otherwise our own quantity=2 URL update gets reset back to 1.
    const quantityRules = getProductQuantityRules(product, querySelectedVariant);
    setSelectedQuantity(normalizeProductQuantity(selectedQuantity, quantityRules));

    setMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, queryString]);

  // on each change of selected values, quantity or variant, update url
  useEffect(() => {
    if (!isMounted) return;
    const newSearchParams = new URLSearchParams(window.location.search);
    syncVariantIdToParams(selectedVariant, newSearchParams);
    const quantityToSet = normalizeProductQuantity(
      selectedQuantity,
      getProductQuantityRules(product, selectedVariant),
    );
    newSearchParams.set('quantity', String(quantityToSet));

    // shallow routing instantly updates url
    window.history.replaceState(
      {},
      '',
      `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}${window.location.hash}`,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, selectedValues, selectedQuantity, isMounted]);

  return null;
}
